import { ReactNode, useState } from 'react'
import { Menu as MenuIcon, Edit as EditIcon } from '@mui/icons-material'
import { MenuItem, Menu, styled, Typography, IconButton, Box, Button } from '@mui/material'
import Stack from './Stack.common'
import {
  Action,
  Action as ActionInterface,
  Actions as ActionsInterface,
  MenuAction,
} from '../../models/props.models'

const OptionLabel = styled(Typography)<{ color?: string }>(({ color, theme }) => ({
  fontSize: '0.75rem',
  color: color
    ? (theme.palette as any)[color]?.main || (theme.palette as any)[color] || color
    : 'inherit',
  fontWeight: 500,
}))

interface ActionsProps {
  action: ActionsInterface
}

const MenuItems: React.FC<{ items: Action[]; setAnchorEl: any; group?: boolean }> = ({
  items,
  setAnchorEl,
  group,
}) => {
  return (
    <Box>
      {items.map((item, i) => (
        <MenuItem
          key={i}
          onClick={(evt) => {
            evt.stopPropagation()
            item.onClick(evt)
            setAnchorEl(null)
          }}>
          {item.custom ? (
            item.custom
          ) : (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              padding={group ? '0 18px' : undefined}>
              {item.icon}
              <OptionLabel color={item.color}> {item.label}</OptionLabel>
            </Stack>
          )}
        </MenuItem>
      ))}
    </Box>
  )
}

const Actions: React.FC<ActionsProps> = ({ action }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const { items, ...actionProps } = action as MenuAction
  const isMenu = items?.length > 0
  const groupedItems = isMenu
    ? items.reduce(
        (
          groupedItems: {
            groupLabel?: string
            color?: string
            items: (Action & { group?: string })[]
          }[],
          item,
        ) => {
          let group = groupedItems.find((group) => item.group === group.groupLabel)
          if (!group) {
            group = {
              color: item.color,
              groupLabel: item.group,
              items: [] as (Action & { group?: string })[],
            }
            groupedItems.push(group)
          }
          group.items.push(item)

          return groupedItems
        },
        [],
      )
    : []
  const base: ActionInterface = (
    isMenu
      ? {
          ...(!actionProps.label && !actionProps.icon
            ? { icon: <MenuIcon color="primary" /> }
            : {}),
          ...actionProps,
          onClick: (event: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget),
        }
      : { icon: <EditIcon />, color: 'primary', ...actionProps }
  ) as ActionInterface

  return (
    <Box>
      <Box
        onClick={(event) => {
          event.stopPropagation()
          base.onClick(event)
        }}>
        {base.custom ? (
          base.custom
        ) : base.label ? (
          <Button variant="contained" color="primary" startIcon={base.icon}>
            {base.label}
          </Button>
        ) : (
          <IconButton color={base.color}>{base.icon}</IconButton>
        )}
      </Box>
      {isMenu && (
        <Menu
          onClick={(event) => event.stopPropagation()}
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={setAnchorEl.bind(null, null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
          {groupedItems.length > 0 &&
            groupedItems.map((group, groupIndex) => (
              <>
                {group.groupLabel && (
                  <Typography
                    key={group.groupLabel}
                    color={group.color}
                    fontSize="18"
                    fontWeight="700"
                    padding="12px 12px 0 12px">
                    {group.groupLabel}
                  </Typography>
                )}
                <MenuItems
                  key={'items' + groupIndex}
                  items={group.items}
                  group
                  setAnchorEl={setAnchorEl}
                />
              </>
            ))}
        </Menu>
      )}
    </Box>
  )
}
export default Actions
